import { useEffect, useCallback, useMemo, useRef } from 'react';
import {
  KnowledgeBaseCategory,
  KnowledgeBaseEntity,
  KnowledgeBaseCategoriesQuery,
  KnowledgeBasesListQuery,
  QUERY_FETCH_KNOWLEDGE_BASE_CATEGORIES,
  QUERY_FETCH_KNOWLEDGE_BASE_LIST,
  QUERY_KNOWLEDGE_BASE_TYPES_WITH_PUBLISHED_ITEMS,
  QUERY_VALID_CATEGORY_IDS_FOR_KNOWLEDGE_BASE_TYPE,
  type FetchKnowledgeBaseTypesWithPublishedItemsQuery,
  type ValidCategoryIdsForKnowledgeBaseTypeQuery,
} from '@edwin-group/people-graphql';
import { useQuery, useSuspenseQuery, useApolloClient } from '@apollo/client';
import { useKnowledgeBaseListStore } from '../store/useKnowledgeBaseListStore';

const PAGE_SIZE = 4;

type CategoryNode = NonNullable<NonNullable<KnowledgeBaseCategoriesQuery['knowledgeBaseCategories']>[number]>;

const mapCategoryNode = (node: CategoryNode): KnowledgeBaseCategory => {
  const subCategoriesNodes = node.subCategories?.nodes
    ?.filter((subNode): subNode is NonNullable<typeof subNode> => subNode !== null)
    .map(mapCategoryNode);

  return {
    documentId: node.documentId,
    category: node.category,
    subCategories: subCategoriesNodes && subCategoriesNodes.length > 0 ? subCategoriesNodes : [],
  } as KnowledgeBaseCategory;
};

const findCategoryById = (categories: KnowledgeBaseCategory[], id: string): KnowledgeBaseCategory | undefined => {
  for (const cat of categories) {
    if (cat.documentId === id) return cat;
    if (cat.subCategories?.length) {
      const filteredSubCategories = cat.subCategories.filter((sub): sub is KnowledgeBaseCategory => sub !== null);
      const found = findCategoryById(filteredSubCategories, id);
      if (found) return found;
    }
  }
  return undefined;
};

const collectAllChildCategoryIds = (categories: KnowledgeBaseCategory[]): Set<string> => {
  const childIds = new Set<string>();
  const collectChildren = (cats: KnowledgeBaseCategory[]) => {
    cats.forEach((cat) => {
      if (cat.subCategories?.length) {
        cat.subCategories.forEach((sub) => {
          if (sub) {
            childIds.add(sub.documentId);
          }
        });
        collectChildren(cat.subCategories.filter((sub): sub is KnowledgeBaseCategory => sub !== null));
      }
    });
  };
  collectChildren(categories);
  return childIds;
};

const collectAllDescendantIds = (category: KnowledgeBaseCategory): string[] => {
  const ids: string[] = [category.documentId];
  if (category.subCategories?.length) {
    for (const sub of category.subCategories) {
      if (sub) {
        ids.push(...collectAllDescendantIds(sub as KnowledgeBaseCategory));
      }
    }
  }
  return ids;
};

export const useKnowledgeBaseList = () => {
  const pagination = useKnowledgeBaseListStore((state) => state.pagination);
  const categories = useKnowledgeBaseListStore((state) => state.categories);
  const selectedCategoryIds = useKnowledgeBaseListStore((state) => state.selectedCategoryIds);
  const selectedSubcategoryIds = useKnowledgeBaseListStore((state) => state.selectedSubcategoryIds);

  const setKnowledgeBases = useKnowledgeBaseListStore((state) => state.setKnowledgeBases);
  const appendKnowledgeBases = useKnowledgeBaseListStore((state) => state.appendKnowledgeBases);
  const setCategories = useKnowledgeBaseListStore((state) => state.setCategories);
  const setRootCategories = useKnowledgeBaseListStore((state) => state.setRootCategories);
  const setValidCategoriesByType = useKnowledgeBaseListStore((state) => state.setValidCategoriesByType);
  const setPagination = useKnowledgeBaseListStore((state) => state.setPagination);
  const setLoading = useKnowledgeBaseListStore((state) => state.setLoading);

  const client = useApolloClient();
  const { data: typesData } = useSuspenseQuery<FetchKnowledgeBaseTypesWithPublishedItemsQuery>(
    QUERY_KNOWLEDGE_BASE_TYPES_WITH_PUBLISHED_ITEMS,
  );

  const { data: categoriesData } = useSuspenseQuery<KnowledgeBaseCategoriesQuery>(
    QUERY_FETCH_KNOWLEDGE_BASE_CATEGORIES,
  );

  useEffect(() => {
    const types = typesData?.knowledgeBaseTypesWithPublishedItems ?? [];
    if (types.length === 0) {
      setValidCategoriesByType({});
      return;
    }

    const loadValidCategoriesByType = async () => {
      const map: Record<string, string[]> = {};
      await Promise.all(
        types.map(async (t) => {
          const { data } = await client.query<ValidCategoryIdsForKnowledgeBaseTypeQuery>({
            query: QUERY_VALID_CATEGORY_IDS_FOR_KNOWLEDGE_BASE_TYPE,
            variables: { knowledgeBaseTypeDocumentId: t.documentId },
          });
          map[t.documentId] = data?.validCategoryIdsForKnowledgeBaseType ?? [];
        }),
      );
      setValidCategoriesByType(map);
    };

    loadValidCategoriesByType();
  }, [typesData, client, setValidCategoriesByType]);

  useEffect(() => {
    if (categoriesData?.knowledgeBaseCategories) {
      const allCategories = categoriesData.knowledgeBaseCategories
        .filter((cat): cat is CategoryNode => cat !== null)
        .map(mapCategoryNode);
      setCategories(allCategories);

      const childIds = collectAllChildCategoryIds(allCategories);
      const roots = allCategories.filter((cat) => !childIds.has(cat.documentId));
      setRootCategories(roots);
    }
  }, [categoriesData, setCategories, setRootCategories]);

  const categoryDocumentIds = useMemo(() => {
    if (selectedCategoryIds.length === 0) return undefined;

    if (selectedSubcategoryIds.length === 0) {
      const ids = new Set<string>();
      for (const rootId of selectedCategoryIds) {
        const root = findCategoryById(categories, rootId);
        if (root) {
          collectAllDescendantIds(root).forEach((id) => ids.add(id));
        }
      }
      return ids.size > 0 ? Array.from(ids) : undefined;
    }

    const ids = new Set<string>();
    for (const subId of selectedSubcategoryIds) {
      const sub = findCategoryById(categories, subId);
      if (sub) {
        collectAllDescendantIds(sub).forEach((id) => ids.add(id));
      }
    }
    return ids.size > 0 ? Array.from(ids) : undefined;
  }, [selectedCategoryIds, selectedSubcategoryIds, categories]);

  const {
    data: knowledgeBasesData,
    fetchMore,
    loading,
    refetch,
  } = useQuery<KnowledgeBasesListQuery>(QUERY_FETCH_KNOWLEDGE_BASE_LIST, {
    variables: {
      page: 1,
      pageSize: PAGE_SIZE,
      categoryDocumentIds,
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (knowledgeBasesData?.knowledgebases) {
      const nodes = knowledgeBasesData.knowledgebases?.nodes || [];
      const pageInfo = knowledgeBasesData.knowledgebases?.pageInfo;

      setKnowledgeBases(nodes as KnowledgeBaseEntity[]);

      if (pageInfo) {
        setPagination({
          page: pageInfo.page || 1,
          pageCount: pageInfo.pageCount || 1,
          total: pageInfo.total || 0,
          pageSize: pageInfo.pageSize || PAGE_SIZE,
        });
      }
    }
  }, [knowledgeBasesData, setKnowledgeBases, setPagination]);

  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    refetch({
      page: 1,
      pageSize: PAGE_SIZE,
      categoryDocumentIds,
    });
  }, [categoryDocumentIds, refetch]);

  const loadMore = useCallback(async () => {
    const nextPage = pagination.page + 1;
    if (nextPage > pagination.pageCount) return;

    setLoading(true);
    const { data } = await fetchMore({
      variables: {
        page: nextPage,
        pageSize: PAGE_SIZE,
        categoryDocumentIds,
      },
    });

    if (data?.knowledgebases) {
      const nodes = data.knowledgebases?.nodes || [];
      const pageInfo = data.knowledgebases?.pageInfo;

      appendKnowledgeBases(nodes as KnowledgeBaseEntity[]);

      if (pageInfo) {
        setPagination({
          page: pageInfo.page || nextPage,
          pageCount: pageInfo.pageCount || pagination.pageCount,
          total: pageInfo.total || pagination.total,
          pageSize: pageInfo.pageSize || PAGE_SIZE,
        });
      }
    }
    setLoading(false);
  }, [pagination, fetchMore, categoryDocumentIds, appendKnowledgeBases, setPagination, setLoading]);

  const hasMore = pagination.page < pagination.pageCount;

  return {
    loading,
    loadMore,
    hasMore,
    shouldFetch: true,
  };
};
